/**
 * Pure favorites storage helpers — cloud fetch/cache, local v2 read/write, legacy merge.
 * React-agnostic; consumed by FavoritesProvider.
 */
import { EdgeFnError } from '@/lib/edgeFn';
import { getAnonUserId } from '@/lib/userIdentity';
import { createFavorite, listFavorites } from '@/lib/favoritesApi';
import { trackEvent } from '@/lib/analytics';
import { toSlug } from '@/lib/ferrySlug';
import { resolveTranslation } from '@/contexts/LanguageContext';
import { getAppLanguage } from '@/lib/appLanguage';

export interface FerryFavorite {
  id: string;
  samband_slug: string;
  custom_name: string | null;
  notify_departure_soon: boolean;
  notify_delay: boolean;
  notify_cancellation: boolean;
  notify_time_to_leave: boolean;
  position: number;
  created_at?: string;
  updated_at?: string;
  /** Resolved from useFerryRoutes – not persisted */
  from?: string;
  to?: string;
  /** Queued offline add — awaiting cloud sync (Phase 2c 5a). */
  pending?: boolean;
}

export interface FavoriteRoute {
  slug: string;
  from: string;
  to: string;
  savedAt: string;
}

export const CLOUD_CACHE_KEY = 'ferryFavoritesCache_v1';
/**
 * Hvem hurtiglageret tilhører: en bruker-ID, eller 'anon' for anonyme favoritter.
 * Uten dette kunne en kontos favoritter bli vist til neste person i samme nettleser
 * når innloggingen hadde gått ut (overgangen «innlogget → utlogget» skjer da aldri).
 */
export const CLOUD_CACHE_OWNER_KEY = 'ferryFavoritesCacheOwner_v1';
export const ANON_CACHE_OWNER = 'anon';
export const CLOUD_MIGRATED_KEY = 'ferryFavoritesMigrated_v1';
export const ARCHIVE_KEY = 'favoritesLocalDisplayArchived_v1';
/** Når flyttingen til skyen ble fullført (ISO). Lokale favoritter lagret etter dette er nye. */
export const CLOUD_MIGRATED_AT_KEY = 'ferryFavoritesMigratedAt_v1';
export const LEGACY_CLOUD_KEY = 'favoriteFerrySamband_v2';
export const CLOUD_SYNC_EVENT = 'ferryFavoritesUpdated';

export const LOCAL_V2_KEY = 'favoriteFerrySamband_v2';
export const RUTY_FAVORITES_KEY = 'ruty_favorites';
export const RUTY_MIGRATED_KEY = 'ruty_favorites_migrated_v1';
export const FERRY_ROUTES_CACHE_KEY = 'ferry_routes_cache';
export const LOCAL_SYNC_EVENT = 'favoriteRoutesUpdated';
export const PENDING_KEY = 'favorites_pending_v1';
export const PENDING_SYNC_EVENT = 'favoritesPendingUpdated';
export const MAX_LOCAL_FAVORITES = 3;

export interface PendingFavoriteEntry {
  samband_slug: string;
  custom_name?: string | null;
  from?: string;
  to?: string;
  queued_at: string;
}

interface MutateResponse {
  favorite: FerryFavorite;
}

interface LegacyFavorite {
  slug: string;
  from?: string;
  to?: string;
}

/** In-flight GET dedupe — N provider mounts share one network request per anon user. */
const inflightGets = new Map<string, Promise<FerryFavorite[]>>();

/**
 * Bumped by {@link clearInflightCloudFetches} so abandoned GETs cannot resolve
 * into a later test/mount after the Map entry was dropped.
 */
let cloudFetchGeneration = 0;

/**
 * Test helper — drop shared in-flight GETs and invalidate any promise still
 * running so suites cannot inherit hung or late-resolving cloud state.
 */
export function clearInflightCloudFetches(): void {
  inflightGets.clear();
  cloudFetchGeneration += 1;
}

/** Synchronous anon id — creates and persists on first call. */
export function resolveAnonUserId(): string {
  return getAnonUserId();
}

/**
 * Waits until anon user id is readable (browser + storage ready).
 * Provider uses this before the first cloud fetch.
 *
 * Uses real wall-clock deadlines via `Date.now` — under Vitest fake timers,
 * callers must `useRealTimers()` (or advance timers) or this can hang.
 */
export async function waitForAnonUserId(maxWaitMs = 2000): Promise<string> {
  if (typeof window === 'undefined') {
    throw new Error('[favoritesStore] waitForAnonUserId requires browser');
  }
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const id = getAnonUserId();
    if (id.length > 0) return id;
    await new Promise((resolve) => setTimeout(resolve, 16));
  }
  return getAnonUserId();
}

export function normalizeSambandSlug(slug: string): string {
  return toSlug(slug);
}

function normalizeFavorite(favorite: FerryFavorite): FerryFavorite {
  return {
    ...favorite,
    samband_slug: normalizeSambandSlug(favorite.samband_slug),
  };
}

export function normalizeFavorites(favorites: FerryFavorite[]): FerryFavorite[] {
  return favorites
    .map(normalizeFavorite)
    .filter((favorite) => favorite.samband_slug.length > 0);
}

function isFerryFavorite(value: unknown): value is FerryFavorite {
  if (typeof value !== 'object' || value === null) return false;
  return (
    'id' in value
    && typeof value.id === 'string'
    && 'samband_slug' in value
    && typeof value.samband_slug === 'string'
    && 'custom_name' in value
    && (typeof value.custom_name === 'string' || value.custom_name === null)
    && 'notify_departure_soon' in value
    && typeof value.notify_departure_soon === 'boolean'
    && 'notify_delay' in value
    && typeof value.notify_delay === 'boolean'
    && 'notify_cancellation' in value
    && typeof value.notify_cancellation === 'boolean'
    && 'notify_time_to_leave' in value
    && typeof value.notify_time_to_leave === 'boolean'
    && 'position' in value
    && typeof value.position === 'number'
  );
}

function isLegacyFavorite(value: unknown): value is LegacyFavorite {
  return typeof value === 'object'
    && value !== null
    && 'slug' in value
    && typeof value.slug === 'string'
    && value.slug.length > 0;
}

function isFavoriteRoute(value: unknown): value is FavoriteRoute {
  return (
    typeof value === 'object'
    && value !== null
    && typeof (value as FavoriteRoute).slug === 'string'
    && (value as FavoriteRoute).slug.length > 0
    && typeof (value as FavoriteRoute).from === 'string'
    && typeof (value as FavoriteRoute).to === 'string'
  );
}

export function readCloudCache(): FerryFavorite[] {
  try {
    const raw = localStorage.getItem(CLOUD_CACHE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? normalizeFavorites(parsed.filter(isFerryFavorite)) : [];
  } catch {
    return [];
  }
}

/** Read-only cloud cache access for observability / gates without mounting the provider. */
export function readFavoritesCache(): FerryFavorite[] {
  return readCloudCache();
}

export function readCloudCacheOwner(): string | null {
  try {
    return localStorage.getItem(CLOUD_CACHE_OWNER_KEY);
  } catch {
    return null;
  }
}

/** Hurtiglageret bare hvis det tilhører `owner`. Ukjent eier (eldre lager) regnes som fremmed. */
export function readCloudCacheFor(owner: string): FerryFavorite[] {
  return readCloudCacheOwner() === owner ? readCloudCache() : [];
}

export function writeCloudCache(favs: FerryFavorite[], owner?: string): void {
  try {
    if (owner) localStorage.setItem(CLOUD_CACHE_OWNER_KEY, owner);
    localStorage.setItem(CLOUD_CACHE_KEY, JSON.stringify(normalizeFavorites(favs)));
    window.dispatchEvent(new CustomEvent(CLOUD_SYNC_EVENT));
  } catch { /* ignore quota */ }
}

/** Clear server snapshot cache (e.g. on sign-out). Writes [] so empty is explicit, not "missing". */
export function clearCloudCache(): void {
  writeCloudCache([], ANON_CACHE_OWNER);
}

function readLegacyCloud(): LegacyFavorite[] {
  try {
    const raw = localStorage.getItem(LEGACY_CLOUD_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isLegacyFavorite) : [];
  } catch {
    return [];
  }
}

/** Setter «flyttet til skyen» og husker når. Bruk alltid denne, aldri setItem direkte. */
export function markCloudMigratedNow(): void {
  try {
    localStorage.setItem(CLOUD_MIGRATED_KEY, '1');
    localStorage.setItem(CLOUD_MIGRATED_AT_KEY, new Date().toISOString());
  } catch { /* ignore quota / private mode */ }
}

/** Når flyttingen ble fullført, eller null for eldre brukere som ble flyttet før dette ble lagret. */
export function readCloudMigratedAt(): number | null {
  try {
    const raw = localStorage.getItem(CLOUD_MIGRATED_AT_KEY);
    if (!raw) return null;
    const ms = Date.parse(raw);
    return Number.isNaN(ms) ? null : ms;
  } catch {
    return null;
  }
}

/** True after one-time legacy→cloud migration has completed. */
export function isCloudMigrated(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(CLOUD_MIGRATED_KEY) === '1';
  } catch {
    return false;
  }
}

/** True when local v2 must not contribute to the displayed favorites list. Never unset. */
export function isLocalDisplayArchived(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(ARCHIVE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setLocalDisplayArchived(): void {
  try {
    localStorage.setItem(ARCHIVE_KEY, '1');
  } catch { /* ignore quota / private mode */ }
}

/**
 * Set display archive after a successful cloud apply — never from error fallbacks.
 * Requires isCloudMigrated(); unmigrated users are never archived.
 */
export function maybeSetLocalDisplayArchived(
  anonUserId: string,
  sessionUserId: string | null | undefined,
  isMergeCompleteForPair: (anon: string, uid: string) => boolean,
  hasMergeMarker: boolean,
  /** Favorittene serveren nettopp svarte med (ikke den sammenslåtte visningen). */
  serverFavorites: FerryFavorite[],
): void {
  if (!isCloudMigrated()) return;
  if (isLocalDisplayArchived()) return;

  // Arkiver aldri en lokal favoritt som ikke har kommet til skyen.
  // Favoritter lagret lokalt ETTER flyttingen (for eksempel fra en eldre appversjon
  // i en annen fane) legges i køen for opplasting, og arkiveringen venter til de
  // finnes i skyen. Favoritter som fantes ved flyttingen og nå mangler i skyen,
  // er slettet på en annen enhet, og skal ikke lastes opp igjen.
  const migratedAt = readCloudMigratedAt();
  if (migratedAt === null) {
    // Eldre bruker uten tidspunkt: vi kan ikke skille nye fra slettede.
    // Start klokka nå, så favoritter lagret fra nå av blir beskyttet.
    markCloudMigratedNow();
  } else {
    const inCloud = new Set(serverFavorites.map((f) => normalizeSambandSlug(f.samband_slug)));
    const notUploaded = readStoredLocalRoutesRaw().filter((route) => {
      const slug = resolveLegacySambandSlug(route);
      const savedMs = Date.parse(route.savedAt);
      return slug.length > 0 && !inCloud.has(slug) && Number.isFinite(savedMs) && savedMs > migratedAt;
    });
    if (notUploaded.length > 0) {
      for (const route of notUploaded) {
        enqueuePending({ samband_slug: resolveLegacySambandSlug(route), from: route.from, to: route.to });
      }
      return;
    }
  }

  if (sessionUserId) {
    if (isMergeCompleteForPair(anonUserId, sessionUserId)) {
      setLocalDisplayArchived();
    }
    return;
  }

  if (!hasMergeMarker) {
    setLocalDisplayArchived();
  }
}

/**
 * Legacy LEGACY_CLOUD_KEY rows for cloud-cache display merge (unmigrated only).
 * Skipped once migration flag is set — migrated users use cloud cache + archive
 * (Commit 4). readLegacyCloud() remains for Phase 2 migration upload.
 * mergedList also reads local v2 via syncLocalRoutesFromStorage (separate path).
 */
function readLegacyCloudForDisplay(): FerryFavorite[] {
  if (isCloudMigrated()) return [];
  return normalizeLegacyFavorites(readLegacyCloud());
}

export function resolveLegacySambandSlug(item: LegacyFavorite): string {
  const resolvedFromSlug = normalizeSambandSlug(item.slug);
  if (resolvedFromSlug.length > 0) return resolvedFromSlug;

  if (item.from && item.to) {
    return toSlug(`${item.from}-${item.to}`);
  }

  return '';
}

export function normalizeLegacyFavorites(legacy: LegacyFavorite[]): FerryFavorite[] {
  return legacy.flatMap((item, index) => {
    const resolvedSambandSlug = resolveLegacySambandSlug(item);

    if (import.meta.env.DEV) {
      console.debug('[favoritesStore] normalized legacy favorite', {
        rawFavorite: item,
        resolvedSambandSlug,
      });
    }

    if (resolvedSambandSlug.length === 0) return [];

    return [{
      id: `legacy-${resolvedSambandSlug}`,
      samband_slug: resolvedSambandSlug,
      custom_name: null,
      notify_departure_soon: true,
      notify_delay: true,
      notify_cancellation: true,
      notify_time_to_leave: false,
      position: index,
      from: item.from,
      to: item.to,
    }];
  });
}

export function mergeCloudFavorites(primary: FerryFavorite[], secondary: FerryFavorite[]): FerryFavorite[] {
  const merged = new Map<string, FerryFavorite>();

  for (const favorite of normalizeFavorites(secondary)) {
    merged.set(favorite.samband_slug, favorite);
  }

  for (const favorite of normalizeFavorites(primary)) {
    merged.set(favorite.samband_slug, favorite);
  }

  return Array.from(merged.values()).sort((a, b) => a.position - b.position);
}

/**
 * Shared cloud read: warm cache → authoritative empty (migrated) → legacy (unmigrated only).
 * `owner` er den som er logget inn nå (bruker-ID eller 'anon'). Før innloggingen er
 * avklart vises bare anonymt hurtiglager, aldri en kontos.
 */
function readCloudFavoritesFromStorage(owner: string = ANON_CACHE_OWNER): FerryFavorite[] {
  const cached = readCloudCacheFor(owner);
  if (cached.length > 0) return cached;
  if (isCloudMigrated()) return [];
  return readLegacyCloudForDisplay();
}

export function getInitialCloudFavorites(): FerryFavorite[] {
  return readCloudFavoritesFromStorage(ANON_CACHE_OWNER);
}

export function fetchCloudFavorites(anonUserId: string): Promise<FerryFavorite[]> {
  const existing = inflightGets.get(anonUserId);
  if (existing) return existing;

  const generation = cloudFetchGeneration;
  const promise = listFavorites(anonUserId)
    .then((data) => {
      // clearInflightCloudFetches() bumped generation — drop stale rows so a later
      // mount/test cannot inherit favorites from an abandoned in-flight GET.
      if (generation !== cloudFetchGeneration) return [];
      return data.favorites;
    })
    .finally(() => {
      // Only remove our entry — a newer GET for the same anon must stay deduped.
      if (inflightGets.get(anonUserId) === promise) {
        inflightGets.delete(anonUserId);
      }
    });

  inflightGets.set(anonUserId, promise);
  return promise;
}

export async function fetchAndMergeCloudFavorites(anonUserId: string): Promise<{
  cloudFavorites: FerryFavorite[];
  merged: FerryFavorite[];
  fetchError?: Error;
}> {
  // Aldri reserve fra en kontos hurtiglager her (kan være en annen person sitt).
  const cached = readCloudCacheFor(ANON_CACHE_OWNER);
  const localLegacy = readLegacyCloudForDisplay();

  try {
    const cloudFavorites = await fetchCloudFavorites(anonUserId);
    const merged = cloudFavorites.length > 0
      ? mergeCloudFavorites(cloudFavorites, localLegacy)
      : mergeCloudFavorites(cached, localLegacy);
    return { cloudFavorites, merged };
  } catch (err) {
    const fallback = mergeCloudFavorites(cached, localLegacy);
    return {
      cloudFavorites: [],
      merged: fallback,
      fetchError: err instanceof Error ? err : new Error(resolveTranslation(getAppLanguage(), 'favorites_fetch_failed')),
    };
  }
}

/** @deprecated Use runPhase2Migration from favoritesMigration instead. */
export async function migrateLegacyCloudFavorites(
  anonUserId: string,
  cloudFavorites: FerryFavorite[],
): Promise<FerryFavorite[]> {
  if (typeof window === 'undefined') return cloudFavorites;
  if (isCloudMigrated()) return cloudFavorites;

  const legacy = readLegacyCloud();
  if (legacy.length === 0) {
    markCloudMigratedNow();
    return cloudFavorites;
  }

  const cloudBySlug = new Set(
    cloudFavorites.map((favorite) => normalizeSambandSlug(favorite.samband_slug)),
  );
  const pending = legacy.filter((item) => !cloudBySlug.has(resolveLegacySambandSlug(item)));

  if (pending.length === 0) {
    markCloudMigratedNow();
    return cloudFavorites;
  }

  for (let i = 0; i < pending.length; i++) {
    const item = pending[i];
    try {
      await createFavorite(
        {
          samband_slug: resolveLegacySambandSlug(item),
          position: cloudFavorites.length + i,
        },
        anonUserId,
      );
    } catch (err) {
      console.warn('[favoritesStore] migration failed for', item.slug, err);
      return cloudFavorites;
    }
  }

  markCloudMigratedNow();
  trackEvent('ferry_favorites_migrated', { count: pending.length });
  const data = await listFavorites(anonUserId);
  return data.favorites.length > 0 ? data.favorites : cloudFavorites;
}

/** Raw v2 list from localStorage only (no DEV preview seed, no MAX cap). */
export function readStoredLocalRoutesRaw(): FavoriteRoute[] {
  try {
    const raw = localStorage.getItem(LOCAL_V2_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isFavoriteRoute);
  } catch {
    return [];
  }
}

/** Raw v2 list from localStorage only (no DEV preview seed). */
export function readStoredLocalRoutes(): FavoriteRoute[] {
  return readStoredLocalRoutesRaw().slice(0, MAX_LOCAL_FAVORITES);
}

function readRutyFavoriteSlugs(): string[] {
  try {
    const raw = localStorage.getItem(RUTY_FAVORITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const slugs = parsed.filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
    return [...new Set(slugs)];
  } catch {
    return [];
  }
}

function lookupRouteNames(slug: string): { from: string; to: string } {
  try {
    const raw = sessionStorage.getItem(FERRY_ROUTES_CACHE_KEY);
    if (!raw) return { from: '', to: '' };
    const cached = JSON.parse(raw) as { routes?: Array<{ slug: string; from: string; to: string }> };
    const match = cached.routes?.find((r) => r.slug === slug);
    if (match) return { from: match.from, to: match.to };
  } catch { /* ignore */ }
  return { from: '', to: '' };
}

function migrateRutyFavoritesOnce(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(RUTY_MIGRATED_KEY)) return;

    const rutySlugs = readRutyFavoriteSlugs();
    if (rutySlugs.length === 0) {
      localStorage.setItem(RUTY_MIGRATED_KEY, '1');
      return;
    }

    const rutySlugSet = new Set(rutySlugs);
    let merged = readStoredLocalRoutes();
    let changed = false;
    const dropped: string[] = [];
    let hasUnresolvedNames = false;

    merged = merged.map((fav) => {
      if (!rutySlugSet.has(fav.slug)) return fav;
      const { from, to } = lookupRouteNames(fav.slug);
      const nextFrom = fav.from || from;
      const nextTo = fav.to || to;
      if (!nextFrom || !nextTo) hasUnresolvedNames = true;
      if (nextFrom === fav.from && nextTo === fav.to) return fav;
      changed = true;
      return { ...fav, from: nextFrom, to: nextTo };
    });

    const mergedSlugs = new Set(merged.map((f) => f.slug));

    for (const slug of rutySlugs) {
      if (mergedSlugs.has(slug)) continue;
      if (merged.length >= MAX_LOCAL_FAVORITES) {
        dropped.push(slug);
        continue;
      }

      const { from, to } = lookupRouteNames(slug);
      if (!from || !to) hasUnresolvedNames = true;
      merged.push({
        slug,
        from,
        to,
        savedAt: new Date().toISOString(),
      });
      mergedSlugs.add(slug);
      changed = true;
    }

    if (dropped.length > 0) {
      console.warn(
        '[favoritesStore] ruty_favorites migration dropped slugs beyond MAX_LOCAL_FAVORITES:',
        dropped,
      );
    }

    if (changed) {
      writeLocalRoutes(merged);
    }

    if (!hasUnresolvedNames) {
      localStorage.setItem(RUTY_MIGRATED_KEY, '1');
    }
  } catch (err) {
    console.warn('[favoritesStore] ruty_favorites migration failed', err);
  }
}

export function readLocalRoutes(): FavoriteRoute[] {
  migrateRutyFavoritesOnce();
  try {
    const raw = localStorage.getItem(LOCAL_V2_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_LOCAL_FAVORITES) : [];
  } catch {
    return [];
  }
}

export function writeLocalRoutes(favs: FavoriteRoute[]): void {
  try {
    localStorage.setItem(LOCAL_V2_KEY, JSON.stringify(favs.slice(0, MAX_LOCAL_FAVORITES)));
    window.dispatchEvent(new CustomEvent(LOCAL_SYNC_EVENT));
  } catch { /* ignore quota */ }
}

export function syncCloudFavoritesFromStorage(owner: string = ANON_CACHE_OWNER): FerryFavorite[] {
  return readCloudFavoritesFromStorage(owner);
}

export function syncLocalRoutesFromStorage(): FavoriteRoute[] {
  return readLocalRoutes();
}

function isPendingFavoriteEntry(value: unknown): value is PendingFavoriteEntry {
  return (
    typeof value === 'object'
    && value !== null
    && 'samband_slug' in value
    && typeof (value as PendingFavoriteEntry).samband_slug === 'string'
    && (value as PendingFavoriteEntry).samband_slug.length > 0
    && 'queued_at' in value
    && typeof (value as PendingFavoriteEntry).queued_at === 'string'
  );
}

function writePendingQueue(entries: PendingFavoriteEntry[]): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(entries));
    window.dispatchEvent(new CustomEvent(PENDING_SYNC_EVENT));
  } catch { /* ignore quota */ }
}

export function readPendingQueue(): PendingFavoriteEntry[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isPendingFavoriteEntry)
      .map((entry) => ({
        ...entry,
        samband_slug: normalizeSambandSlug(entry.samband_slug),
        custom_name: entry.custom_name ?? null,
      }))
      .filter((entry) => entry.samband_slug.length > 0);
  } catch {
    return [];
  }
}

/** Idempotent enqueue — skips if slug already queued. */
export function enqueuePending(entry: Omit<PendingFavoriteEntry, 'queued_at'>): PendingFavoriteEntry[] {
  try {
    const slug = normalizeSambandSlug(entry.samband_slug);
    if (slug.length === 0) return readPendingQueue();

    const queue = readPendingQueue();
    if (queue.some((item) => item.samband_slug === slug)) {
      return queue;
    }

    const next: PendingFavoriteEntry = {
      samband_slug: slug,
      custom_name: entry.custom_name ?? null,
      from: entry.from,
      to: entry.to,
      queued_at: new Date().toISOString(),
    };
    const updated = [...queue, next];
    writePendingQueue(updated);
    return updated;
  } catch {
    return readPendingQueue();
  }
}

export function dequeuePending(slug: string): PendingFavoriteEntry[] {
  try {
    const normalized = normalizeSambandSlug(slug);
    const updated = readPendingQueue().filter((item) => item.samband_slug !== normalized);
    writePendingQueue(updated);
    return updated;
  } catch {
    return readPendingQueue();
  }
}

export function clearPending(): void {
  try {
    writePendingQueue([]);
  } catch { /* ignore */ }
}

export function syncPendingFromStorage(): PendingFavoriteEntry[] {
  return readPendingQueue();
}

/** FIFO order for pending sync drain (oldest queued_at first). */
export function sortPendingFifo(queue: PendingFavoriteEntry[]): PendingFavoriteEntry[] {
  return [...queue].sort((a, b) => a.queued_at.localeCompare(b.queued_at));
}

/** Display label for pending sync toasts — mirrors MyFerriesSection naming. */
export function pendingEntryDisplayName(entry: PendingFavoriteEntry): string {
  const custom = entry.custom_name?.trim();
  if (custom) return custom;
  const route = [entry.from, entry.to].filter(Boolean).join('–');
  return route.length > 0 ? route : entry.samband_slug;
}

/** Drop pending rows whose slug already exists in cloud (another tab synced). */
export function prunePendingAlreadyInCloud(cloudSlugs: Iterable<string>): PendingFavoriteEntry[] {
  try {
    const slugSet = new Set<string>();
    for (const slug of cloudSlugs) {
      const normalized = normalizeSambandSlug(slug);
      if (normalized.length > 0) slugSet.add(normalized);
    }
    const updated = readPendingQueue().filter((entry) => !slugSet.has(entry.samband_slug));
    writePendingQueue(updated);
    return updated;
  } catch {
    return readPendingQueue();
  }
}

export function pendingEntryToFerryFavorite(
  entry: PendingFavoriteEntry,
  position: number,
): FerryFavorite {
  return {
    id: `pending-${entry.samband_slug}`,
    samband_slug: entry.samband_slug,
    custom_name: entry.custom_name ?? null,
    notify_departure_soon: true,
    notify_delay: true,
    notify_cancellation: true,
    notify_time_to_leave: false,
    position,
    from: entry.from,
    to: entry.to,
    pending: true,
  };
}

/**
 * Distinct samband slugs for free-limit gating — cloud ∪ pending, deduped.
 * Import filterCloudSambandFavorites at call site to avoid circular deps with resolution.
 */
export function countDistinctFavoriteSlugs(
  cloudSlugs: Iterable<string>,
  pendingQueue: PendingFavoriteEntry[],
): number {
  const slugs = new Set<string>();
  for (const slug of cloudSlugs) {
    const normalized = normalizeSambandSlug(slug);
    if (normalized.length > 0) slugs.add(normalized);
  }
  for (const entry of pendingQueue) {
    if (entry.samband_slug.length > 0) slugs.add(entry.samband_slug);
  }
  return slugs.size;
}

const OFFLINE_NETWORK_MESSAGE = /failed to fetch|networkerror|load failed/i;

/** instanceof-safe across duplicate module instances (e.g. vitest mocks). */
function asEdgeFnError(err: unknown): EdgeFnError | null {
  if (err instanceof EdgeFnError) return err;
  if (
    err instanceof Error
    && err.name === 'EdgeFnError'
    && typeof (err as EdgeFnError).code === 'string'
  ) {
    return err as EdgeFnError;
  }
  return null;
}

/**
 * True when the server deliberately rejected the request (do NOT enqueue offline).
 * Invariant: no HTTP status = we never reached a deciding server (not deliberate here).
 */
export function isDeliberateFavoriteServerRejection(err: EdgeFnError): boolean {
  if (err.code === 'LIMIT_REACHED') return true;
  if (err.status !== undefined && err.status >= 400 && err.status < 500) return true;
  return false;
}

/**
 * True when a failed cloud favorite POST should enqueue offline (not HTTP rejection).
 *
 * Based on edgeFn's actual throw surface (see edgeFn.ts catch/finalize paths):
 * - AbortError → EdgeFnError TIMEOUT
 * - fetch TypeError → EdgeFnError UNKNOWN { details: TypeError }
 * - invoke failure → EdgeFnError EDGE_FN_ERROR (no status)
 * - GET 5xx → EdgeFnError EDGE_FN_ERROR { status: 5xx } after retries exhaust
 * - envelope ok:false → EdgeFnError { code: business code }
 *
 * navigator.onLine is an additional positive signal only — never the sole gate.
 */
function classifyOfflineEdgeFnError(err: EdgeFnError): boolean {
  if (isDeliberateFavoriteServerRejection(err)) {
    return false;
  }

  if (err.code === 'TIMEOUT' || err.code === 'ABORTED') {
    return true;
  }

  if (OFFLINE_NETWORK_MESSAGE.test(err.message)) {
    return true;
  }

  if (err.details instanceof TypeError) {
    return true;
  }
  if (err.code === 'UNKNOWN' && err.details instanceof TypeError) {
    return true;
  }

  if (err.status === undefined || err.status === 0) {
    return true;
  }

  if (err.status >= 500) {
    return true;
  }

  return false;
}

export function isOfflineFavoriteWriteError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  if (err instanceof TypeError) {
    return true;
  }

  if (err instanceof DOMException && err.name === 'AbortError') {
    return true;
  }

  const edgeErr = err instanceof EdgeFnError ? err : asEdgeFnError(err);
  if (edgeErr) {
    return classifyOfflineEdgeFnError(edgeErr);
  }

  return false;
}

export type { MutateResponse };
